import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Button, Card, CardActionArea, CardContent, Grid,
  CircularProgress, LinearProgress, Chip,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Add, Flag } from 'iconsax-react';
import { useObjectiveStore } from '../../store/objective.store';
import { useTeamStore } from '../../store/team.store';
import { useToast, TOAST_TYPES } from '../../providers/ToastProvider';
import { getApiErrorDetail } from '../../utils/taskApi.utils';
import {
  filterObjectives,
  formatObjectiveAmount,
  getUniqueObjectiveOwners,
  ObjectiveDueDateFilter,
  ownerFilterKey,
} from '../../utils/objective.utils';
import { Objective } from '../../types/objective.type';
import { CreateObjectiveRequest, UpdateObjectiveRequest } from '../../types/objective.type';
import ObjectiveFormDialog from '../../components/objectives/ObjectiveFormDialog';
import TasksToolbar from '../../components/tasks/TasksToolbar';

function ProgressBar({ objective }: { objective: Objective }) {
  const pct = Math.min(100, Math.max(0, objective.progress_percentage ?? 0));
  const color = pct >= 100 ? '#10B981' : pct >= 50 ? '#3B82F6' : '#F59E0B';
  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', mb: 0.5 }}>
        <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color }}>
          {formatObjectiveAmount(pct)}%
        </Typography>
        <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
          {formatObjectiveAmount(objective.achieved_value)}/{formatObjectiveAmount(objective.target_value)}
        </Typography>
      </Box>
      <LinearProgress
        variant="determinate"
        value={pct}
        sx={{
          height: 8,
          borderRadius: 4,
          bgcolor: '#E5E7EB',
          '& .MuiLinearProgress-bar': { bgcolor: color, borderRadius: 4 },
        }}
      />
    </Box>
  );
}

const DUE_FILTER_LABELS: Record<Exclude<ObjectiveDueDateFilter, ''>, string> = {
  overdue: 'objectives.dueFilters.overdue',
  thisWeek: 'objectives.dueFilters.thisWeek',
  thisMonth: 'objectives.dueFilters.thisMonth',
  noDue: 'objectives.dueFilters.noDue',
};

export default function ObjectivesPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { objectives, isLoading, fetchObjectives, addObjective } = useObjectiveStore();
  const { teams, fetchTeams } = useTeamStore();
  const [search, setSearch] = useState('');
  const [teamFilter, setTeamFilter] = useState('');
  const [ownerFilter, setOwnerFilter] = useState('');
  const [dueDateFilter, setDueDateFilter] = useState<ObjectiveDueDateFilter>('');
  const [formOpen, setFormOpen] = useState(false);
  const locale = i18n.language === 'ar' ? 'ar-SA' : 'en-US';

  useEffect(() => {
    fetchObjectives();
    fetchTeams();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const ownerOptions = useMemo(() => getUniqueObjectiveOwners(objectives), [objectives]);

  const filtered = useMemo(
    () => filterObjectives(objectives, {
      search,
      teamId: teamFilter,
      ownerKey: ownerFilter,
      dueDateFilter,
    }),
    [objectives, search, teamFilter, ownerFilter, dueDateFilter],
  );

  const hasActiveFilters = !!(search || teamFilter || ownerFilter || dueDateFilter);

  const activeChips = useMemo(() => {
    const chips: { key: string; label: string; onRemove: () => void }[] = [];
    if (teamFilter) {
      const label = teams.find(team => team.id === teamFilter)?.name ?? '';
      chips.push({ key: 'team', label: `${t('objectives.filters.team')}: ${label}`, onRemove: () => setTeamFilter('') });
    }
    if (ownerFilter) {
      const owner = ownerOptions.find(o => ownerFilterKey(o) === ownerFilter);
      chips.push({ key: 'owner', label: `${t('objectives.filters.owner')}: ${owner?.name ?? ''}`, onRemove: () => setOwnerFilter('') });
    }
    if (dueDateFilter) {
      chips.push({
        key: 'due',
        label: t(DUE_FILTER_LABELS[dueDateFilter]),
        onRemove: () => setDueDateFilter(''),
      });
    }
    return chips;
  }, [teamFilter, ownerFilter, dueDateFilter, teams, ownerOptions, t]);

  const handleCreate = async (data: CreateObjectiveRequest | UpdateObjectiveRequest) => {
    if (!data.title?.trim()) return;
    try {
      const payload: CreateObjectiveRequest = {
        title: data.title.trim(),
        team_id: data.team_id ?? undefined,
        metric: data.metric ?? undefined,
        target_value: data.target_value ?? undefined,
        due_date: data.due_date ?? undefined,
        owners: data.owners,
      };
      const created = await addObjective(payload);
      showToast(t('objectives.createdSuccess'), TOAST_TYPES.SUCCESS);
      navigate(`/dashboard/objectives/${created.id}`);
    } catch (e) {
      showToast(getApiErrorDetail(e, t('common.error')), TOAST_TYPES.ERROR);
      throw e;
    }
  };

  const clearFilters = () => {
    setSearch('');
    setTeamFilter('');
    setOwnerFilter('');
    setDueDateFilter('');
  };

  const filterGroups = useMemo(() => [
    {
      key: 'team',
      label: t('objectives.filters.team'),
      value: teamFilter,
      allLabel: t('objectives.allTeams'),
      options: teams.map(team => ({ id: team.id, label: team.name })),
      onChange: setTeamFilter,
      hidden: teams.length === 0,
    },
    {
      key: 'owner',
      label: t('objectives.filters.owner'),
      value: ownerFilter,
      allLabel: t('objectives.allOwners'),
      options: ownerOptions.map(owner => ({
        id: ownerFilterKey(owner),
        label: owner.name,
      })),
      onChange: setOwnerFilter,
      hidden: ownerOptions.length === 0,
    },
    {
      key: 'due',
      label: t('objectives.filters.dueDate'),
      value: dueDateFilter,
      allLabel: t('objectives.dueFilters.all'),
      options: [
        { id: 'overdue', label: t('objectives.dueFilters.overdue') },
        { id: 'thisWeek', label: t('objectives.dueFilters.thisWeek') },
        { id: 'thisMonth', label: t('objectives.dueFilters.thisMonth') },
        { id: 'noDue', label: t('objectives.dueFilters.noDue') },
      ],
      onChange: (v: string) => setDueDateFilter(v as ObjectiveDueDateFilter),
    },
  ], [teamFilter, ownerFilter, dueDateFilter, teams, ownerOptions, t]);

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, mb: 3 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, color: 'text.primary', letterSpacing: '-0.3px' }}>
            {t('objectives.title')}
          </Typography>
          <Typography sx={{ color: 'text.secondary', fontSize: '0.875rem', mt: 0.5 }}>
            {t('objectives.subtitle', { count: objectives.length })}
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<Add size={18} color="#fff" />}
          onClick={() => setFormOpen(true)}
          sx={{ textTransform: 'none', flexShrink: 0 }}
        >
          {t('objectives.createObjective')}
        </Button>
      </Box>

      <TasksToolbar
        searchValue={search}
        searchPlaceholder={t('objectives.searchPlaceholder')}
        onSearchChange={setSearch}
        filterGroups={filterGroups}
        activeChips={activeChips}
        onClearAll={clearFilters}
        filteredCount={filtered.length}
        totalCount={objectives.length}
        clearFiltersLabel={t('objectives.clearFilters')}
        showingCountLabel="objectives.showingCount"
      />

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : filtered.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 10 }}>
          <Flag size={48} color="#CBD5E1" variant="Bulk" />
          <Typography sx={{ color: 'text.secondary', mt: 2 }}>
            {hasActiveFilters ? t('objectives.noResults') : t('objectives.noObjectives')}
          </Typography>
          {hasActiveFilters ? (
            <Button size="small" onClick={clearFilters} sx={{ mt: 2, textTransform: 'none' }}>
              {t('objectives.clearFilters')}
            </Button>
          ) : (
            <Button
              variant="contained"
              startIcon={<Add size={16} color="#fff" />}
              onClick={() => setFormOpen(true)}
              sx={{ mt: 2, textTransform: 'none' }}
            >
              {t('objectives.createObjective')}
            </Button>
          )}
        </Box>
      ) : (
        <Grid container spacing={2.5}>
          {filtered.map(objective => (
            <Grid item xs={12} sm={6} lg={4} key={objective.id}>
              <Card
                sx={{
                  height: '100%',
                  border: '1px solid #E2E8F0',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                  transition: 'all 0.2s',
                  '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 8px 30px rgba(0,0,0,0.08)' },
                }}
              >
                <CardActionArea
                  onClick={() => navigate(`/dashboard/objectives/${objective.id}`)}
                  sx={{ height: '100%' }}
                >
                  <CardContent sx={{ p: 2.5, height: '100%', display: 'flex', flexDirection: 'column' }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 1.5 }}>
                      <Box
                        sx={{
                          width: 40, height: 40, borderRadius: 2, flexShrink: 0,
                          bgcolor: '#EEF2FF', display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}
                      >
                        <Flag size={20} color="#6366F1" variant="Bold" />
                      </Box>
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography sx={{ fontWeight: 700, fontSize: '0.9375rem', lineHeight: 1.3 }}>
                          {objective.title}
                        </Typography>
                        {objective.team_name && (
                          <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', mt: 0.25 }}>
                            {objective.team_name}
                          </Typography>
                        )}
                      </Box>
                    </Box>

                    {objective.metric && (
                      <Typography
                        sx={{
                          fontSize: '0.8125rem', color: 'text.secondary', mb: 1.5,
                          overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box',
                          WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
                        }}
                      >
                        {objective.metric}
                      </Typography>
                    )}

                    {(objective.owners ?? []).length > 0 && (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1.5 }}>
                        {objective.owners!.slice(0, 3).map(owner => (
                          <Chip
                            key={ownerFilterKey(owner)}
                            label={owner.name}
                            size="small"
                            sx={{ height: 22, fontSize: '0.7rem', bgcolor: '#F1F5F9', color: '#64748B' }}
                          />
                        ))}
                        {objective.owners!.length > 3 && (
                          <Chip
                            label={`+${objective.owners!.length - 3}`}
                            size="small"
                            sx={{ height: 22, fontSize: '0.7rem', bgcolor: '#F1F5F9', color: '#64748B' }}
                          />
                        )}
                      </Box>
                    )}

                    <Box sx={{ mt: 'auto' }}>
                      <ProgressBar objective={objective} />
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 1.5 }}>
                        <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                          {t('objectives.tasksProgress', {
                            completed: objective.tasks_completed,
                            total: objective.tasks_total,
                          })}
                        </Typography>
                        {objective.due_date && (
                          <Chip
                            label={new Date(objective.due_date).toLocaleDateString(locale, {
                              month: 'short',
                              day: 'numeric',
                            })}
                            size="small"
                            sx={{ height: 22, fontSize: '0.7rem', bgcolor: '#F1F5F9', color: '#64748B' }}
                          />
                        )}
                      </Box>
                    </Box>
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      <ObjectiveFormDialog
        open={formOpen}
        onClose={() => setFormOpen(false)}
        onSubmit={handleCreate}
      />
    </Box>
  );
}
